// components/calendar/CalendarWeekView.js
// Vista settimanale stile Teamleader con griglia oraria

import React, { useMemo } from 'react';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, getWeek, addDays } from 'date-fns';
import { it } from 'date-fns/locale';
import { isItalianHoliday, isSunday, isSaturday, createHolidayMap, formatDateKey } from '../../utils/italianHolidays';

import { buildCalendarEventTitle } from './calendarEventFormatter';
// Orari visualizzati (6:00 - 24:00)
const HOURS = Array.from({ length: 19 }, (_, i) => i + 6);
const HOUR_HEIGHT = 56; // px per ora - aumentato per leggibilità

// Colori per tipo appuntamento - più vivaci
const APPOINTMENT_COLORS = {
  sopralluogo: 'bg-[#4A90A4] border-[#3A7A94] text-white',
  sede: 'bg-[#5B9BD5] border-[#4A8AC4] text-white',
  presentazione: 'bg-[#5B9BD5] border-[#4A8AC4] text-white',
  scelta_extra: 'bg-[#70AD47] border-[#5F9C36] text-white',
  personale: 'bg-[#7F7F7F] border-[#6F6F6F] text-white',
  interno: 'bg-[#7F7F7F] border-[#6F6F6F] text-white',
  google_external: 'bg-[#DB4437] border-[#C33326] text-white',
  annullato: 'bg-[#D9D9D9] border-[#C9C9C9] text-[#737373] line-through',
};

const CalendarWeekView = ({
  currentDate,
  appointments,
  leads,
  onEventClick,
  onTimeSlotClick,
}) => {
  // Calcola i giorni della settimana
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 }); // Lunedì
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });
  const weekNumber = getWeek(currentDate, { weekStartsOn: 1 });

  // Mappa festività
  const holidayMap = useMemo(() => {
    const year = currentDate.getFullYear();
    return {
      ...createHolidayMap(year),
      ...createHolidayMap(year + 1),
    };
  }, [currentDate]);

  // Ottieni appuntamenti per un giorno specifico
  const getAppointmentsForDay = (date) => {
    return appointments.filter(apt => 
      isSameDay(new Date(apt.appointment_date), date) &&
      apt.status !== 'annullato' // Escludi annullati dalla griglia principale
    ).sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date));
  };

  // Ottieni info lead
  const getLeadInfo = (leadId) => {
    return leads?.find(l => l.id === leadId);
  };

  // Costruisci titolo evento (utility condivisa: venue snapshot-aware)
  const buildEventTitle = (apt) => buildCalendarEventTitle(apt, getLeadInfo(apt.lead_id));

  // Calcola posizione e altezza evento nella griglia
  const getEventStyle = (apt) => {
    const aptDate = new Date(apt.appointment_date);
    const hour = aptDate.getHours();
    const minutes = aptDate.getMinutes();
    const duration = apt.duration_minutes || 60;
    
    // Top: distanza dall'ora 6:00 (prima ora visualizzata)
    const topOffset = ((hour - 6) * 60 + minutes) / 60;
    const heightUnits = duration / 60;
    
    return {
      top: `${topOffset * HOUR_HEIGHT}px`,
      height: `${Math.max(heightUnits * HOUR_HEIGHT, 28)}px`, // min 28px
      minHeight: '28px',
    };
  };

  // Ottieni colore evento
  const getEventColor = (apt) => {
    if (apt.status === 'annullato') return APPOINTMENT_COLORS.annullato;
    if (apt.source === 'google' || apt.is_google_event || apt.appointment_type === 'google_external') {
      return APPOINTMENT_COLORS.google_external;
    }
    return APPOINTMENT_COLORS[apt.appointment_type] || APPOINTMENT_COLORS.personale;
  };

  // Check se è oggi
  const isToday = (date) => isSameDay(date, new Date());

  return (
    <div className="bg-white rounded-lg border border-[#DCDCDC] overflow-hidden">
      {/* Header con giorni della settimana */}
      <div className="grid grid-cols-[70px_repeat(7,1fr)] border-b border-[#DCDCDC] bg-[#FAFAFA]">
        <div className="p-2 text-center text-xs text-[#737373] border-r border-[#DCDCDC]">
          {/* Angolo vuoto */}
        </div>
        {weekDays.map((day, index) => {
          const dateKey = formatDateKey(day);
          const holiday = holidayMap[dateKey];
          const sunday = isSunday(day);
          const saturday = isSaturday(day);
          const today = isToday(day);
          
          return (
            <div 
              key={format(day, 'yyyy-MM-dd')}
              className={`p-2.5 text-center border-r border-[#DCDCDC] last:border-r-0 ${
                today ? 'bg-[#FF7819]/10' : ''
              }`}
            >
              <div className={`text-xs font-semibold uppercase tracking-wide ${
                sunday ? 'text-red-500' : 
                saturday ? 'text-orange-500' : 
                today ? 'text-[#FF7819]' :
                'text-[#5C5C5C]'
              }`}>
                {format(day, 'EEE', { locale: it })}
              </div>
              <div className={`text-lg font-bold mt-0.5 ${
                today ? 'text-[#FF7819]' : 'text-[#3C3C3C]'
              }`}>
                {format(day, 'd')}
              </div>
              {today && (
                <div className="text-[10px] font-bold text-white bg-[#FF7819] rounded-full px-2 py-0.5 inline-block mt-1">
                  OGGI
                </div>
              )}
              {holiday && (
                <div className="text-[10px] text-red-500 truncate mt-0.5" title={holiday.name}>
                  ⭐ {holiday.name}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Griglia oraria */}
      <div className="relative overflow-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
        <div className="grid grid-cols-[70px_repeat(7,1fr)]">
          {HOURS.map((hour) => (
            <React.Fragment key={hour}>
              {/* Colonna ora */}
              <div 
                className="border-b border-r border-[#E5E5E5] flex items-start justify-end pr-2 pt-1 bg-[#FAFAFA]"
                style={{ height: `${HOUR_HEIGHT}px` }}
              >
                <span className="text-xs font-medium text-[#737373]">
                  {hour.toString().padStart(2, '0')}:00
                </span>
              </div>
              
              {/* Celle per ogni giorno */}
              {weekDays.map((day, dayIndex) => {
                const today = isToday(day);
                const sunday = isSunday(day);
                
                return (
                  <div
                    key={`${hour}-${dayIndex}`}
                    className={`border-b border-r border-[#E5E5E5] last:border-r-0 relative cursor-pointer hover:bg-[#FFF8F0] transition-colors ${
                      today ? 'bg-[#FF7819]/5' : ''
                    } ${sunday ? 'bg-red-50/50' : ''}`}
                    style={{ height: `${HOUR_HEIGHT}px` }}
                    onClick={() => onTimeSlotClick?.(day, hour)}
                    data-testid={`time-slot-${format(day, 'yyyy-MM-dd')}-${hour}`}
                  >
                    {/* Linea tratteggiata per mezz'ora */}
                    <div className="absolute top-1/2 left-0 right-0 border-t border-dashed border-[#E0E0E0]" />
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>

        {/* Eventi sovrapposti alla griglia */}
        <div className="absolute top-0 left-[70px] right-0 pointer-events-none">
          <div className="grid grid-cols-7">
            {weekDays.map((day, dayIndex) => {
              const dayAppointments = getAppointmentsForDay(day);
              
              return (
                <div key={format(day, 'yyyy-MM-dd')} className="relative" style={{ height: `${HOURS.length * HOUR_HEIGHT}px` }}>
                  {dayAppointments.map((apt, aptIndex) => {
                    const style = getEventStyle(apt);
                    const colorClass = getEventColor(apt);
                    const title = buildEventTitle(apt);
                    const aptTime = format(new Date(apt.appointment_date), 'HH:mm');
                    
                    return (
                      <div
                        key={apt.id || aptIndex}
                        className={`absolute left-1 right-1 rounded border-l-4 px-2 py-1 overflow-hidden cursor-pointer pointer-events-auto hover:opacity-90 hover:shadow-md transition-all shadow-sm ${colorClass}`}
                        style={style}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEventClick?.(apt);
                        }}
                        title={`${aptTime} - ${title}`}
                        data-testid={`event-${apt.id}`}
                      >
                        <div className="text-[13px] font-semibold truncate leading-tight flex items-center gap-1">
                          <span className="font-bold">{aptTime}</span>
                          {apt.source === 'google' && (
                            <span className="inline-block w-3 h-3 flex-shrink-0">
                              <svg viewBox="0 0 24 24" className="w-full h-full">
                                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                              </svg>
                            </span>
                          )}
                        </div>
                        {parseInt(style.height) > 40 && (
                          <div className="text-[12px] opacity-90 truncate mt-0.5 font-medium">
                            {title}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarWeekView;
